import React from 'react';
import SplineChartV2, { SplineChartV2Series } from 'src/components/BeaconV2Components/SplineChartV2/SplineChartV2';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { useMetricFormatter } from 'src/utils/Hooks';
import { useTimePeriods } from 'src/utils/Hooks/reduxSelectors';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import LegendWithMetric from 'src/components/BeaconV2Components/Legend/LegendWithMetric';
import BeaconProChart from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/BeaconProChart';
import { VariationsSplineAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/VariationsSplineAdapter';
import { useDynamicVariationsSubtitles } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useDynamicVariationsSubtitles';
import NoDataPlaceHolder, {
  NoDataIcon
} from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import { NewBeaconFileIcon } from 'src/components/SvgIcons';
import { Box } from '@mui/material';

/**
 * Converts TimeSeriesPoint array [weekId, value][] to SplineChartV2Series format
 */
const convertToSplineV2Series = (
  data: [number, number][],
  id: string,
  name: string
): SplineChartV2Series => ({
  id,
  name,
  data: data.map(([weekId, value]) => ({ weekId, value }))
});

interface VariationsParentPageCountSplineChartProps {
  queryFn: () => Promise<VariationsSplineAdapter>;
}

export const VariationsParentPageCountSplineChart = (props: VariationsParentPageCountSplineChartProps) => {
  const { queryFn } = props;
  const { mainTimePeriod, comparisonTimePeriod } = useTimePeriods();
  const { generateVariationsSplineSubtitle } = useDynamicVariationsSubtitles();
  const formatMetric = useMetricFormatter();

  const title = 'Parent Page Count';
  const metricType = METRICTYPE.VOLUME;

  return (
    <BeaconProChart queryFn={queryFn} name="parent-page-count-spline-chart">
      <BeaconProChart.Header>
        <BeaconProChart.Title>
          <Text variant="h4">{title}</Text>
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle
        render={(data: VariationsSplineAdapter) => {
          const metricName = title.toLowerCase();
          if (!data) {
            return generateVariationsSplineSubtitle(metricName);
          }

          const transformedData = data.transform();
          const { primaryLegendMetric, secondaryLegendMetric, percentChange } = transformedData?.legendData || {};

          return generateVariationsSplineSubtitle(metricName, primaryLegendMetric, percentChange, metricType, true);
        }}
      />

      <BeaconProChart.Legend
        render={(data: VariationsSplineAdapter) => {
          const transformedData = data.transform();
          const { primaryLegendMetric, secondaryLegendMetric, percentChange } = transformedData?.legendData || {};

          return (
            <Flex gap="lg" sx={{ marginTop: '32px' }}>
              <LegendWithMetric
                color="casper"
                displayName={comparisonTimePeriod.displayName}
                metric={formatMetric(secondaryLegendMetric, metricType, { decimalPlaces: 0 })}
              />
              <LegendWithMetric
                color="primary"
                displayName={mainTimePeriod.displayName}
                metric={formatMetric(primaryLegendMetric, metricType, { decimalPlaces: 0 })}
                change={formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, { decimalPlaces: 1 })}
                direction={getDirection(percentChange)}
              />
            </Flex>
          );
        }}
      />
      <BeaconProChart.Visualization
        render={(data: VariationsSplineAdapter, isLoading: boolean) => {
          const transformedData = data.transform();
          const { primaryMetrics, secondaryMetrics } = transformedData?.chartData || {};

          const hasNoData =
            !isLoading &&
            (!primaryMetrics || primaryMetrics.length === 0) &&
            (!secondaryMetrics || secondaryMetrics.length === 0);

          if (hasNoData) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '200px',
                  marginTop: '32px'
                }}
              >
                <NoDataPlaceHolder
                  title="Data currently unavailable"
                  description="There is currently no data matching your selected criteria. Please try another query to view insights."
                  icon={<NoDataIcon icon={<NewBeaconFileIcon />} />}
                />
              </Box>
            );
          }

          const primarySeries = convertToSplineV2Series(primaryMetrics || [], 'primary', 'Primary');
          const comparisonSeries = convertToSplineV2Series(secondaryMetrics || [], 'comparison', 'Comparison');

          return (
            <div style={{ marginTop: '32px' }}>
              <SplineChartV2
                primarySeries={primarySeries}
                comparisonSeries={comparisonSeries}
                metricType={metricType}
              />
            </div>
          );
        }}
      />
    </BeaconProChart>
  );
};
